import { Field, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { Alert, Button, Col, Form, Modal, Placeholder, Row, Stack } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { IInvitation } from "../../../../customTypings/user/Invitation";
import { IRole } from "../../../../customTypings/user/Role";
import { invitationService } from "../../../../services";
import { rolesService } from "../../../../services";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    shouldShowFormFieldError,
    showToastError,
    showToastSuccess,
} from "../../../../utils";

type Props = {
    show: boolean;
    invitationId: string;
    onClose: (updated: boolean) => void;
};

type RoleFormValues = { roles: string[] };

const invitationRolesValidationSchema = Yup.object({
    roles: Yup.array()
        .of(Yup.string().required())
        .min(1, "Select at least one role.")
        .required(),
});

const InvitationRoleEdit: React.FC<Props> = ({ show, invitationId, onClose }) => {
    const [alertVariant, setAlertVariant] = useState("danger");
    const [loading, setLoading] = useState(true);
    const [allRoles, setAllRoles] = useState<IRole[]>();
    const [invitation, setInvitation] = useState<IInvitation>();
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const validationSchema = useMemo(() => invitationRolesValidationSchema, []);

    useEffect(() => {
        if (show) {
            setLoading(true);
            Promise.all([rolesService.getAllForGroup(0), invitationService.getById(invitationId)]).then(
                (response: [IRole[], IInvitation]) => {
                    setAllRoles(response[0]);
                    setInvitation(response[1]);
                    setLoading(false);
                }
            );
        }
    }, [invitationId, show]);

    const labelSpan = 3;
    const fieldSpan = 9;

    return (
        <Modal centered show={show} keyboard={false} onHide={() => onClose(false)}>
            {loading || !allRoles || !invitation ? (
                <>
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Roles</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row className="mb-3">
                            <Col md={labelSpan}>Email</Col>
                            <Col md={fieldSpan}>
                                <Skeleton width="20rem" height="2rem" />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={labelSpan}>Organisation</Col>
                            <Col md={fieldSpan}>
                                <Skeleton width="20rem" height="2rem" />
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={labelSpan}>Roles</Col>
                            <Col md={fieldSpan}>
                                <Skeleton width="20rem" height="2rem" />
                                <Skeleton width="20rem" height="2rem" />
                                <Skeleton width="20rem" height="2rem" />
                                <Skeleton width="20rem" height="2rem" />
                                <Skeleton width="20rem" height="2rem" />
                                <Skeleton width="20rem" height="2rem" />
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Placeholder.Button variant="primary" xs={3} className="me-2" />
                        <Placeholder.Button variant="secondary" xs={2} />
                    </Modal.Footer>
                </>
            ) : (
                <Formik<RoleFormValues>
                    enableReinitialize
                    key={invitation.tokenId}
                    initialValues={{
                        roles: invitation.roles.map((role) => role.id),
                    }}
                    validationSchema={validationSchema}
                    validateOnMount={false}
                    validateOnChange
                    validateOnBlur={false}
                    onSubmit={async (values, { setStatus, setSubmitting }) => {
                        setLiveValidationAfterSubmit(true);
                        setStatus(undefined);
                        setSubmitting(true);
                        try {
                            const response: any = await invitationService.updateRoles(
                                invitation.tokenId,
                                values.roles
                            );
                            if (response.status !== "Failure") {
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess("Role updated successfully!");
                                setTimeout(() => onClose(true), 1000);
                            } else {
                                setAlertVariant("danger");
                                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                            }
                        } catch {
                            setAlertVariant("danger");
                            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ status, isSubmitting, handleSubmit, errors, submitCount }) => {
                        const hasPersistedRecord = true;
                        const showErr = (field: keyof RoleFormValues) =>
                            shouldShowFormFieldError(
                                errors,
                                field,
                                hasPersistedRecord,
                                submitCount,
                                liveValidationAfterSubmit
                            );
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Modal.Header closeButton>
                                    <Modal.Title>Edit Roles</Modal.Title>
                                </Modal.Header>
                                <Modal.Body>
                                    <Stack gap={3}>
                                        <Form.Group as={Row} className="mb-3" controlId="email">
                                            <Form.Label column sm={labelSpan}>
                                                Email
                                            </Form.Label>
                                            <Col sm={fieldSpan}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={invitation.email}
                                                    aria-label={invitation.email}
                                                    disabled
                                                    readOnly
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="organisationName">
                                            <Form.Label column sm={labelSpan}>
                                                Organisation
                                            </Form.Label>
                                            <Col sm={fieldSpan}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder={invitation.organisationName}
                                                    aria-label={invitation.organisationName}
                                                    disabled
                                                    readOnly
                                                />
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="roleCheckboxs">
                                            <Form.Label column sm={labelSpan}>
                                                Roles <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Col sm={fieldSpan} className="pt-2">
                                                {allRoles.map((role) => (
                                                    <label key={role.id} className="d-block">
                                                        <Field
                                                            type="checkbox"
                                                            name="roles"
                                                            value={role.id}
                                                            className="form-check-input me-2"
                                                        />
                                                        {role.name}
                                                    </label>
                                                ))}
                                                {showErr("roles") && (
                                                    <div className="invalid-feedback d-block">{errors.roles as string}</div>
                                                )}
                                            </Col>
                                        </Form.Group>
                                        {status && (
                                            <Alert variant={alertVariant} className="mt-3">
                                                {status}
                                            </Alert>
                                        )}
                                    </Stack>
                                </Modal.Body>
                                <Modal.Footer>
                                    <div className="form-group">
                                        <Button variant="primary" disabled={isSubmitting} type="submit">
                                            {isSubmitting ? <LoadingSpinner text="Updating..." /> : "Update"}
                                        </Button>
                                    </div>
                                </Modal.Footer>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </Modal>
    );
};

export { InvitationRoleEdit };
